import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { getGeoName } from '../../../../../common/utils/memo';
import FormDropdown from '../FormDropdown/FormDropdown';
import UserContext from '../../../../../common/utils/getContext';
import classNames from 'classnames';
import OptionTitle from '../components/OptionTitle/OptionTitle';
import css from './FormSelectLocation.module.scss';

const continents = [0, 'continent'];
const countries = [0, 'country_id', 'delivery_country_id', 'country'];
const states = [0, 'region_id', 'delivery_state_id', 'region'];
const cities = [0, 'city_id', 'delivery_city_id', 'city'];
const getIsContinent = (a) => {
    if (continents.indexOf(a) !== -1) {
        return continents.indexOf(a);
    } else {
        return false;
    }
};
const getIsCountry = (a) => {
    if (countries.indexOf(a) !== -1) {
        return countries.indexOf(a);
    } else {
        return false;
    }
};
const getIsState = (a) => {
    if (states.indexOf(a) !== -1) {
        return states.indexOf(a);
    } else {
        return false;
    }
};
const getIsCity = (a) => {
    if (cities.indexOf(a) !== -1) {
        return cities.indexOf(a);
    } else {
        return false;
    }
};
const getDisabled = (attribute, formData, disabled, notDisabled, useContinent) => {
    if (notDisabled) return false;
    if (disabled) {
        return disabled;
    }
    if (getIsContinent(attribute)) return disabled;
    if (getIsCountry(attribute)) {
        if (useContinent) {
            return !Boolean(formData[continents[getIsCountry(attribute)]]);
        } else {
            return disabled;
        }
    }
    if (getIsState(attribute)) {
        return !Boolean(formData[countries[getIsState(attribute)]]);
    }
    if (getIsCity(attribute)) {
        return !Boolean(formData[states[getIsCity(attribute)]]);
    }
};
const FormDropdownLocation = (props) => {
    const {
        attribute,
        disabled,
        notDisabled,
        region,
        value,
        valueId,
        formData,
        setFormData,
        useContinent,
        preparedLoad = true,
        isClear
    } = props;
    const disableField = getDisabled(props.attribute, formData, disabled, notDisabled, useContinent);
    const regionValue = region?.value;
    const regionAttribute = region?.attribute;
    const { userToken, lang } = useContext(UserContext);
    const [val, setVal] = useState(value);
    const [loaded, setLoaded] = useState(value || !valueId);
    const isCityField = getIsCity(props.attribute);
    useEffect(() => {
        if (value && !value?.state_name && regionValue) {
            setVal({ ...value, state_name: regionValue?.label, state_geoname_id: regionValue?.value });
        } else {
            setVal(value);
        }
    }, [value, regionValue]);
    useEffect(() => {
        if (valueId && !value && !loaded) {
            getGeoName(valueId, lang).then((res) => {
                const result = {
                    ...res?.data,
                    id: res?.data?.geoname_id
                };
                setFormData(attribute, result);
                setVal(result);
                setLoaded(true);
            });
        }
    }, [lang, value, valueId, setFormData, loaded, attribute, userToken]);

    const getOptions = getIsContinent(attribute)
        ? {
              url: '/api/v1/geonames/continents',
              preparedLoad: preparedLoad
          }
        : getIsCountry(attribute)
        ? {
              url: useContinent
                  ? `/api/v1/geonames/countries?filter[continent]=${formData['continent']?.id}`
                  : '/api/v1/geonames/countries',
              preparedLoad: preparedLoad
          }
        : getIsState(attribute)
        ? {
              preparedLoad: preparedLoad,
              url: formData[countries[getIsState(props.attribute)]]?.links?.states_of_country || '/api/v1/geonames/states'
          }
        : {
              //   searchMinLength: 3,
              preparedLoad: preparedLoad,
              url: formData[states[getIsCity(props.attribute)]]?.links?.cities_of_state || '/api/v1/geonames/cities',
              searchUrl: formData[states[getIsCity(props.attribute)]]?.links?.cities_of_state || '/api/v1/geonames/cities'
          };

    return (
        <>
            <FormDropdown
                {...props}
                setFormData={(attr, val) => {
                    setVal(val);
                    if (getIsContinent(attribute) && formData && formData[countries[getIsContinent(attribute)]]) {
                        props.setFormData(countries[getIsContinent(attribute)], null);
                    }
                    if (getIsContinent(attribute) && formData && formData[states[getIsContinent(attribute)]]) {
                        props.setFormData(states[getIsContinent(attribute)], null);
                    }
                    if (getIsState(attribute) && formData && formData[cities[getIsState(attribute)]]) {
                        props.setFormData(cities[getIsState(attribute)], null);
                    }
                    if (getIsCountry(attribute) && formData && formData[states[getIsCountry(attribute)]]) {
                        props.setFormData(states[getIsCountry(attribute)], null);
                        props.setFormData(cities[getIsCountry(attribute)], null);
                    }
                    if (regionAttribute) {
                        setFormData(
                            regionAttribute,
                            val?.state_geoname_id
                                ? {
                                      geoname_id: val?.state_geoname_id,
                                      name: val?.state_name,
                                      value: val?.state_geoname_id,
                                      label: val?.state_name
                                  }
                                : null
                        );
                    }
                    setFormData(attr, val);
                }}
                formatter={
                    isCityField
                        ? (opt, props, isOption, searchValue) => {
                              return (
                                  <div
                                      {...props}
                                      className={classNames(props.className, isOption ? css.cityOption : css.cityValue)}
                                  >
                                      <div className={css.cityName}>
                                          <OptionTitle searchValue={searchValue} option={opt} />
                                      </div>
                                      {opt?.state_name ? (
                                          <div className={css.cityState}>
                                              {!isOption ? '(' : ''}
                                              {opt?.state_name}
                                              {!isOption ? ')' : ''}
                                          </div>
                                      ) : null}
                                  </div>
                              );
                          }
                        : false
                }
                value={loaded && val}
                disabled={disableField}
                isClear={isClear !== undefined ? isClear : true}
                getOptions={{ ...getOptions, ...props?.getOptions, id: getIsContinent(attribute) ? 'id' : 'geoname_id' }}
            />
        </>
    );
};
FormDropdownLocation.propTypes = {
    attribute: PropTypes.string,
    setFormData: PropTypes.func,
    handlers: PropTypes.object,
    formData: PropTypes.object
};
export default FormDropdownLocation;
